import type { HistoryPoint } from "../../types/leaderboard";
import { formatTimestamp } from "./format";
import type { HistoryChartPoint } from "./history-chart";

export interface BuildChartDataOptions {
  teamIds?: Set<string>;
  /** Current display names keyed by team id — avoids duplicate lines after renames. */
  teamLabels?: Map<string, string>;
}

function resolveTeamLabel(
  teamId: string,
  history: HistoryPoint[],
  teamLabels?: Map<string, string>,
): string {
  return (
    teamLabels?.get(teamId) ?? history.find((point) => point.teamId === teamId)?.teamName ?? teamId
  );
}

export function buildChartData(
  history: HistoryPoint[],
  options: BuildChartDataOptions = {},
): {
  data: HistoryChartPoint[];
  teams: string[];
} {
  const filtered = options.teamIds
    ? history.filter((point) => options.teamIds?.has(point.teamId))
    : history;

  const teamIds = [...new Set(filtered.map((point) => point.teamId))].sort((a, b) =>
    resolveTeamLabel(a, filtered, options.teamLabels).localeCompare(
      resolveTeamLabel(b, filtered, options.teamLabels),
      "fi",
    ),
  );

  const teams = teamIds.map((teamId) => resolveTeamLabel(teamId, filtered, options.teamLabels));
  const snapshots = [...new Set(filtered.map((point) => point.snapshotId))];

  const data = snapshots
    .map((snapshotId) => {
      const points = filtered.filter((point) => point.snapshotId === snapshotId);
      const createdAt = points[0]?.createdAt;
      if (!createdAt) {
        return null;
      }

      const row: HistoryChartPoint = {
        createdAt: formatTimestamp(createdAt),
        createdAtIso: createdAt,
      };

      for (let index = 0; index < teamIds.length; index += 1) {
        const teamId = teamIds[index];
        const label = teams[index];
        const match = points.find((point) => point.teamId === teamId);
        row[label] = match?.totalScore ?? 0;
      }

      return row;
    })
    .filter((row): row is HistoryChartPoint => row !== null);

  return { data, teams };
}
